import React, { PureComponent } from "react";
import { debounce, get } from "lodash";
import { connect } from "react-redux";
import { omitBy } from "lodash";

import Layout from "../Common/Layout";
import {
  fetchAllInvoicesIds,
  fetchInvoices,
  approveInvoice,
  closeInvoice,
  bulkCloseInvoices,
  bulkExportInvoices,
  bulkNetsuiteApiExportInvoices,
  bulkSimulateExportInvoices,
  bulkApproveInvoices,
  bulkApproveSkipPayment,
  bulkPayInvoices,
  bulkSkipPaymentForInvoices,
  bulkVerifyPaymentInvoices
} from "../../actions/invoices.actions";
import Loader from "../Common/Loader";
import InvoiceList from "./InvoiceList";
import { BULK_ACTIONS, INVOICE_SERVICES, INVOICE_STATUS, IN_PROGRESS_STATUS, SHOW_STATUS, STATUS } from "./constants";
import ResourceList from "../Common/ResourceList";
import { BulkAction } from "../Common/BulkAction";
import { dateToIso, isDateFormatValid } from "../../services/date-helper.service";
import isEmpty from "lodash/isEmpty";
import { bulkDeleteInvoices, promoteInProgressInvoices } from "../../actions/internal-invoices.actions";

import { CLEAR_INVOICES } from "../../actions";

export class InvoiceListPage extends PureComponent {
  title = "Invoices";
  isInternalInvoice = true;
  invoicesService = INVOICE_SERVICES.INTERNAL_INVOICES;

  state = {
    filter: {
      status: STATUS[0],
      paymentStatus: [],
      exportStatus: [],
      customerHasAutopay: "",
      customerHasWalletId: "",
      billingPeriodStartFrom: "",
      billingPeriodStartTo: "",
      billingPeriodEndFrom: "",
      billingPeriodEndTo: "",
      invoiceAmountFrom: "",
      invoiceAmountTo: "",
      exportedPeriodFrom: "",
      exportedPeriodTo: "",
      billingPeriodInProgress: ""
    },
    openedInvoice: null,
    autoFocus: {
      customerNameAutoFocus: false,
      invoiceAmountFromAutoFocus: false,
      invoiceAmountToAutoFocus: false,
      customerHasWalletIdAutoFocus: false
    },
    filterOptions: {
      customerNameFilterByOpen: false,
      customerHasAutopayFilterByOpen: false,
      customerHasWalletIdFilterByOpen: false,
      invoiceAmountFilterByOpen: false,
      billingPeriodFilterByOpen: false
    },
    isAllRecordsSelected: false,
    deselectedRecords: [],
    errors: {}
  };

  addToDeselectedRecords = id => {
    this.setState(prevState => ({
      deselectedRecords: [...prevState.deselectedRecords, id]
    }));
  };

  removeFromDeselectedRecords = id => {
    this.setState({ deselectedRecords: this.state.deselectedRecords.filter(invoiceId => invoiceId !== id) });
  };

  deselectAllInvoices = () => {
    this.setState({ isAllRecordsSelected: false, deselectedRecords: [] });
  };

  componentDidUpdate(prevProps, prevState) {
    const { isAllRecordsSelected, filter } = this.state;

    if (
      (this.props.tenantSlug && this.props.tenantSlug !== prevProps.tenantSlug) ||
      (this.props.isBackendProcessingOngoing !== prevProps.isBackendProcessingOngoing &&
        prevProps.isBackendProcessingOngoing)
    ) {
      this.fetchList(1);
    }

    if (isAllRecordsSelected && prevState.isAllRecordsSelected !== isAllRecordsSelected) {
      const filter = omitBy({ ...this.state.filter }, v => v === "");
      this.props.dispatch(fetchAllInvoicesIds(filter, this.invoicesService));
    }

    if (prevState.filter !== filter) this.deselectAllInvoices();
  }

  onOpen = id => {
    const { openedInvoice } = this.state;

    this.setState({ openedInvoice: openedInvoice === id ? null : id });
  };

  listOfInvoicesIdOnly = () => {
    const { listOfAllInvoicesIdOnly } = this.props;
    const { deselectedRecords } = this.state;

    return listOfAllInvoicesIdOnly.map(invoice => invoice.id).filter(invoiceId => !deselectedRecords.includes(invoiceId));
  };

  handleBulkAction = (action, getItems = false) => {
    const { isAllRecordsSelected } = this.state;

    switch (action) {
      case BULK_ACTIONS.APPROVE:
        return () =>
          this.props.dispatch(
            bulkApproveSkipPayment(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              isAllRecordsSelected,
              this.invoicesService
            )
          );
      case BULK_ACTIONS.APPROVE_NO_PAY:
        return () =>
          this.props.dispatch(
            bulkApproveSkipPayment(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              isAllRecordsSelected,
              this.invoicesService
            )
          );
      case BULK_ACTIONS.APPROVE_PAY:
        return () =>
          this.props.dispatch(
            bulkApproveInvoices(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              isAllRecordsSelected,
              {},
              this.invoicesService
            )
          );
      case BULK_ACTIONS.RETRY_PAYMENT:
        return () =>
          this.props.dispatch(
            bulkPayInvoices(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              isAllRecordsSelected,
              {},
              this.invoicesService
            )
          );
      case BULK_ACTIONS.SKIP_PAYMENT:
        return () =>
          this.props.dispatch(
            bulkSkipPaymentForInvoices(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              isAllRecordsSelected,
              this.invoicesService
            )
          );
      case BULK_ACTIONS.VERIFY_PAYMENT:
        return () =>
          this.props.dispatch(
            bulkVerifyPaymentInvoices(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              isAllRecordsSelected,
              {},
              this.invoicesService
            )
          );
      case BULK_ACTIONS.CLOSE:
        const transitionOnly = this.state.filter.status === "paymentPending";

        return () =>
          this.props.dispatch(
            bulkCloseInvoices(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              transitionOnly,
              isAllRecordsSelected,
              this.invoicesService
            )
          );
      case BULK_ACTIONS.SIMULATE_EXPORT:
        return () =>
          this.props.dispatch(
            bulkSimulateExportInvoices(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              isAllRecordsSelected,
              this.invoicesService
            )
          );
      case BULK_ACTIONS.EXPORT:
        return () =>
          this.props.dispatch(
            bulkExportInvoices(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              isAllRecordsSelected,
              {},
              this.invoicesService
            )
          );
      case BULK_ACTIONS.NETSUITE_API_EXPORT:
        return () => {
          this.props.dispatch(
            bulkNetsuiteApiExportInvoices(
              isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(),
              this.invoicesService,
              isAllRecordsSelected
            )
          );
        };
      case "Delete":
        return () =>
          this.props.dispatch(
            bulkDeleteInvoices(isAllRecordsSelected ? this.listOfInvoicesIdOnly() : getItems(), isAllRecordsSelected)
          );
      case "Promote": {
        const filter = omitBy({ ...this.state.filter }, v => v === "");

        return () => this.props.dispatch(promoteInProgressInvoices(getItems(), { filter }));
      }
      default:
        return () => {};
    }
  };

  fetchList = (page, options = {}) => {
    const filter = omitBy({ ...this.state.filter }, v => v === "");

    this.props.dispatch(fetchInvoices({ page: { number: page }, filter }, this.invoicesService, options));
  };

  fetchListDebounced = debounce(this.fetchList, 800);

  applyFilter = status => (e, data) => {
    this.props.dispatch({
      type: CLEAR_INVOICES
    });

    this.setState(
      {
        filter: { status },
        filterOptions: {
          customerHasAutopayFilterByOpen: false,
          customerHasWalletIdFilterByOpen: false,
          customerNameFilterByOpen: false,
          invoiceAmountFilterByOpen: false,
          billingPeriodFilterByOpen: false
        }
      },
      this.fetchListDebounced(1)
    );
  };

  triggerFetchAllInvoicesIds = isAllRecordsSelected => {
    this.setState({ isAllRecordsSelected, deselectedRecords: [] });
  };

  filterInvoices = (e, { name, value }) => {
    const { filter } = this.state;
    this.setState(
      { filter: { ...filter, [name]: value }, autoFocus: { [`${name}AutoFocus`]: true } },
      this.fetchListDebounced(1, { performRequest: true })
    );
  };

  filterByExportedPeriod = (e, { name, value }) => {
    const { filter } = this.state;
    const exportedPeriodFrom = value.split(" - ")[0];
    const exportedPeriodTo = value.split(" - ")[1];
    this.setState(
      { filter: { ...filter, [`${name}From`]: exportedPeriodFrom, [`${name}To`]: exportedPeriodTo } },
      this.fetchListDebounced(1)
    );
  };

  filterByBillingPeriod = (e, { name, value }) => {
    const { filter } = this.state;
    const dateIsValid_ = isDateFormatValid(value);
    let date = isEmpty(value) ? "" : dateToIso(value);
    if (dateIsValid_ || value.length === 0) {
      this.setState(
        { filter: { ...filter, [name]: date }, autoFocus: { [`${name}AutoFocus`]: true } },
        this.fetchListDebounced(1, { performRequest: true })
      );
    }
  };

  handleApprove = id => {
    this.props.dispatch(approveInvoice(id, this.invoicesService));
  };

  handleClose = id => {
    this.props.dispatch(closeInvoice(id, this.invoicesService));
  };

  handlePromote = () => {
    this.props.dispatch(promoteInProgressInvoices());
  };

  toggleFilter = (name, isOpen) => {
    if (isOpen) {
      if (name === "billingPeriod")
        this.setState(
          {
            filterOptions: { ...this.state.filterOptions, [`${name}FilterByOpen`]: false },
            filter: {
              ...this.state.filter,
              billingPeriodStartFrom: "",
              billingPeriodStartTo: "",
              billingPeriodEndFrom: "",
              billingPeriodEndTo: ""
            }
          },
          this.fetchListDebounced(1)
        );
      else if (name === "invoiceAmount")
        this.setState(
          {
            filterOptions: { ...this.state.filterOptions, [`${name}FilterByOpen`]: false },
            filter: { ...this.state.filter, invoiceAmountFrom: "", invoiceAmountTo: "" }
          },
          this.fetchListDebounced(1)
        );
      else
        this.setState(
          {
            filterOptions: { ...this.state.filterOptions, [`${name}FilterByOpen`]: false },
            filter: { ...this.state.filter, [name]: "" }
          },
          this.fetchListDebounced(1)
        );
    } else
      this.setState({
        filterOptions: { ...this.state.filterOptions, [`${name}FilterByOpen`]: true },
        autoFocus: { [`${name}FromAutoFocus`]: true }
      });
  };

  errorHandling = (name, message) => {
    this.setState(
      {
        errors: {
          ...this.state.errors,
          [name]: message
        }
      },
      debounce(this.cleanErrorDisplay, 1500)
    );
  };

  cleanErrorDisplay = () => {
    this.setState({
      errors: {}
    });
  };

  getSelectedBillingPeriod = () => {
    const frequency = this.props.invoicingFrequency;
    const selectedDate = this.state.filter.billingPeriodInProgress;

    if (selectedDate && this.state.filter.status === IN_PROGRESS_STATUS && this.isInternalInvoice) {
      const year = selectedDate.split("-")[0];
      const month = selectedDate.split("-")[1];

      if (frequency === 12) {
        return `Year ${year}`;
      } else if (frequency === 3) {
        return `Q${Math.floor((month - 1) / 3) + 1} ${year}`;
      } else if (frequency === 1) {
        return `${month} ${year}`;
      } else {
        return "";
      }
    } else {
      return "";
    }
  };

  render() {
    const { filter, openedInvoice, filterOptions, errors, autoFocus, deselectedRecords } = this.state;

    const {
      isDeliveryEnabled,
      postPaymentsActive,
      totalResourceNumber,
      isFetchingInProgress,
      isBackendProcessingOngoing,
      isNetsuiteApiImportEnabled
    } = this.props;

    const statusesToShow = isDeliveryEnabled ? STATUS : STATUS.filter(x => x !== INVOICE_STATUS.DELIVERED);

    const selectAllInvoicesProps = {
      deselectedRecords: deselectedRecords,
      addToDeselectedRecords: this.addToDeselectedRecords,
      removeFromDeselectedRecords: this.removeFromDeselectedRecords,
      isBackendProcessingOngoing: isBackendProcessingOngoing,
      isFetchingInProgress: isFetchingInProgress,
      triggerFetchAllInvoicesIds: this.triggerFetchAllInvoicesIds,
      totalResourceNumber: totalResourceNumber
    };

    const selectedBillingPeriod = this.getSelectedBillingPeriod();

    return (
      <Layout.Page>
        <Layout.Header title={this.title}>
          {statusesToShow.map(status => (
            <Layout.Filter onFilter={this.applyFilter(status)} active={filter.status === status} key={status}>
              <span data-test={`tab_${status}`}>{SHOW_STATUS[status]}</span>
            </Layout.Filter>
          ))}
        </Layout.Header>
        <Layout.Content>
          <ResourceList
            resourceType={this.resourceType}
            fetch={this.fetchListDebounced}
            loader={<Loader key="__Invoice_Loader" message="Loading invoices..." />}
            filter={filter}
          >
            {list => (
              <BulkAction
                isItemSelectable={id =>
                  !isEmpty(list.find(inv => inv.id === id && inv.status !== INVOICE_STATUS.PROCESSED_BY_USER))
                }
                items={list.map(inv => inv.id)}
              >
                {bulkProps => (
                  <InvoiceList
                    selectAllInvoicesProps={selectAllInvoicesProps}
                    statusFilter={filter.status}
                    invoices={list}
                    filter={filter}
                    filterInvoices={this.filterInvoices}
                    filterByBillingPeriod={this.filterByBillingPeriod}
                    filterByExportedPeriod={this.filterByExportedPeriod}
                    handleApprove={this.handleApprove}
                    handleClose={this.handleClose}
                    bulk={bulkProps}
                    postPaymentsActive={postPaymentsActive}
                    onOpen={this.onOpen}
                    openedInvoice={openedInvoice}
                    handleBulkAction={this.handleBulkAction}
                    handlePromote={this.handlePromote}
                    autoFocus={autoFocus}
                    toggleFilter={this.toggleFilter}
                    filterOptions={filterOptions}
                    errorHandling={this.errorHandling}
                    errors={errors}
                    isInternalInvoice={this.isInternalInvoice}
                    selectedBillingPeriod={selectedBillingPeriod}
                    isNetsuiteApiImportEnabled={isNetsuiteApiImportEnabled}
                  />
                )}
              </BulkAction>
            )}
          </ResourceList>
        </Layout.Content>
      </Layout.Page>
    );
  }
}

const mapStateToProps = state => ({
  tenantSlug: get(state, "common.currentTenant.slug"),
  postPaymentsActive: get(state, "common.currentTenant.postPaymentsActive", false),
  isDeliveryEnabled: get(state, "common.currentTenant.deliveryEnabled", false),
  totalResourceNumber: get(state, "resources.totalResourceNumber"),
  listOfAllInvoicesIdOnly: get(state, "resources.listOfAllResourceIdOnly", []),
  isFetchingInProgress: get(state, "resources.isFetchingInProgress", false),
  isBackendProcessingOngoing: get(state, "resources.isBackendProcessingOngoing", false)
});

export default connect(mapStateToProps)(InvoiceListPage);
